import type {BusinessPolicySnapshot, ComboPricingRule} from '../policy/business-policy.ts';
import type {PolicyValidationResult} from './policy-publish.ts';

export interface ComboPolicyRevisionInput {
  readonly policyVersion: string;
  readonly pricingRevision: string;
  readonly rulesRevision: string;
  readonly comboRules: readonly ComboPricingRule[];
}

const text = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
const record = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const validateComponentPricing = (value: unknown, ruleId: string): string[] => {
  if (value === undefined) return [];
  if (!record(value)) return [`COMBO_COMPONENT_PRICING_INVALID:${ruleId}`];
  const entries = Object.entries(value);
  if (entries.length === 0) return [`COMBO_COMPONENT_PRICING_REQUIRED:${ruleId}`];
  const issues: string[] = [];
  for (const [productId, amountMinor] of entries) {
    if (!text(productId) || !Number.isSafeInteger(amountMinor) || Number(amountMinor) < 0) {
      issues.push(`COMBO_COMPONENT_PRICING_INVALID:${ruleId}:${productId || 'unknown'}`);
    }
  }
  return issues;
};

const validateRule = (rule: unknown, index: number): string[] => {
  if (!record(rule)) return [`COMBO_RULE_INVALID:${index}`];
  const issues: string[] = [];
  const id = text(rule.ruleId) ? rule.ruleId : String(index);
  if (!text(rule.ruleId)) issues.push(`COMBO_RULE_ID_REQUIRED:${index}`);
  if (typeof rule.enabled !== 'boolean') issues.push(`COMBO_RULE_ENABLED_INVALID:${id}`);
  if (!Number.isSafeInteger(rule.priority) || Number(rule.priority) < 0) issues.push(`COMBO_RULE_PRIORITY_INVALID:${id}`);
  if (!Number.isSafeInteger(rule.fixedBaseTotalMinor) || Number(rule.fixedBaseTotalMinor) < 0) issues.push(`COMBO_RULE_TOTAL_INVALID:${id}`);
  issues.push(...validateComponentPricing(rule.componentProductMinor, id));
  if (!Array.isArray(rule.requirements) || rule.requirements.length === 0) {
    issues.push(`COMBO_RULE_REQUIREMENTS_REQUIRED:${id}`);
  } else {
    const selectors = new Set<string>();
    for (const [requirementIndex, requirement] of rule.requirements.entries()) {
      if (!record(requirement) || !text(requirement.selectorKey)) {
        issues.push(`COMBO_SELECTOR_REQUIRED:${id}:${requirementIndex}`);
        continue;
      }
      if (selectors.has(requirement.selectorKey)) issues.push(`COMBO_SELECTOR_DUPLICATE:${id}:${requirement.selectorKey}`);
      selectors.add(requirement.selectorKey);
      if (!Number.isSafeInteger(requirement.quantity) || Number(requirement.quantity) < 1) {
        issues.push(`COMBO_QUANTITY_INVALID:${id}:${requirement.selectorKey}`);
      }
    }
  }
  return issues;
};

export function validateComboPolicyPayload(payload: unknown): PolicyValidationResult {
  if (!record(payload)) return {ok: false, issues: ['BUSINESS_POLICY_REQUIRED']};
  const issues: string[] = [];
  for (const key of ['policyVersion', 'menuRevision', 'pricingRevision', 'availabilityRevision', 'rulesRevision', 'currency'] as const) {
    if (!text(payload[key])) issues.push(`BUSINESS_POLICY_${key.toUpperCase()}_REQUIRED`);
  }
  if (!record(payload.pricing)) issues.push('BUSINESS_POLICY_PRICING_REQUIRED');
  if (!record(payload.visibility)) issues.push('BUSINESS_POLICY_VISIBILITY_REQUIRED');
  if (!record(payload.availability)) issues.push('BUSINESS_POLICY_AVAILABILITY_REQUIRED');
  if (!Array.isArray(payload.modifierRules)) issues.push('BUSINESS_POLICY_MODIFIER_RULES_REQUIRED');
  if (!Array.isArray(payload.comboRules)) {
    issues.push('BUSINESS_POLICY_COMBO_RULES_REQUIRED');
  } else {
    const ids = new Set<string>();
    payload.comboRules.forEach((rule, index) => {
      issues.push(...validateRule(rule, index));
      if (record(rule) && text(rule.ruleId)) {
        if (ids.has(rule.ruleId)) issues.push(`COMBO_RULE_DUPLICATE:${rule.ruleId}`);
        ids.add(rule.ruleId);
      }
    });
  }
  return {ok: issues.length === 0, issues};
}

export function buildComboPolicyRevision(
  active: BusinessPolicySnapshot,
  input: ComboPolicyRevisionInput,
): BusinessPolicySnapshot {
  if (!text(input.policyVersion)) throw new Error('COMBO_POLICY_VERSION_REQUIRED');
  if (!text(input.pricingRevision)) throw new Error('COMBO_PRICING_REVISION_REQUIRED');
  if (!text(input.rulesRevision)) throw new Error('COMBO_RULES_REVISION_REQUIRED');
  const candidate: BusinessPolicySnapshot = {
    ...structuredClone(active),
    policyVersion: input.policyVersion.trim(),
    pricingRevision: input.pricingRevision.trim(),
    rulesRevision: input.rulesRevision.trim(),
    comboRules: structuredClone(input.comboRules),
  };
  const validation = validateComboPolicyPayload(candidate);
  if (!validation.ok) throw new Error(`COMBO_POLICY_INVALID:${validation.issues.join('|')}`);
  return candidate;
}
